import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Plus, Trash2, Save, Edit3, X, Upload, Music, ImageIcon, Video, Sparkles, Film, Youtube, Mic } from "lucide-react";
import TabAudioRecorder from "@/components/admin/TabAudioRecorder";
import LiveSalesTicker from "@/components/admin/LiveSalesTicker";

const EMPTY_SHOW = {
  artist: "",
  era: "",
  slot: "00:00 — 02:00",
  start_iso: "2026-02-01T00:00:00Z",
  image: "https://images.unsplash.com/photo-1704253797699-f983e3cd70ff",
  tagline: "",
  bio: "",
  status: "upcoming",
  venue: "vegas",
  ppv_price_usd: 0,
  is_original: false,
  trailer_url: "",
  character_description: "",
  genre: "",
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [shows, setShows] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null); // show object being edited or "new"
  const [form, setForm] = useState(EMPTY_SHOW);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      return ["shows", "music", "proof", "reservations"].includes(t) ? t : "shows";
    } catch (e) { return "shows"; }
  });

  const refresh = async () => {
    const [s, r, st] = await Promise.all([
      http.get("/admin/shows"),
      http.get("/admin/reservations"),
      http.get("/admin/stats"),
    ]);
    setShows(s.data);
    setReservations(r.data);
    setStats(st.data);
  };

  useEffect(() => { refresh().catch(() => toast.error("Failed to load admin data.")); }, []);

  // Sora 2 queue status — polls every 5s while there is active work
  const [queueStatus, setQueueStatus] = useState(null);

  // Real-track lip-sync BATCH status
  const lipsyncBatchRef = useRef(null);
  const [lipsyncBatchId, setLipsyncBatchId] = useState(() => {
    try { return localStorage.getItem("lipsync_batch_id") || null; } catch { return null; }
  });
  const [lipsyncBatch, setLipsyncBatch] = useState(null);
  useEffect(() => {
    if (!lipsyncBatchId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await http.get(`/admin/hedra/batches/${lipsyncBatchId}`);
        if (!cancelled) setLipsyncBatch(data);
        if (data?.status === "complete") {
          try { localStorage.removeItem("lipsync_batch_id"); } catch {}
          await refresh();
        }
      } catch (e) { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(t); };
  }, [lipsyncBatchId]);
  useEffect(() => {
    let alive = true;
    let lastDone = 0;
    const tick = async () => {
      try {
        const { data } = await http.get("/admin/video-queue/status");
        if (!alive) return;
        // If a job just completed (done count went up), refresh shows so the new
        // trailer_url shows up in the cards instantly.
        if (data.done > lastDone && lastDone > 0) {
          refresh().catch(() => {});
        }
        lastDone = data.done;
        setQueueStatus(data);
      } catch (e) { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const startNew = () => { setEditing("new"); setForm(EMPTY_SHOW); };
  const startNewOriginal = () => {
    setEditing("new");
    setForm({
      ...EMPTY_SHOW,
      is_original: true,
      character_description: "",
      genre: "",
      tagline: "Original hologram act — exclusively at Hologram Club.",
      status: "upcoming",
    });
  };
  const startEdit = (s) => { setEditing(s.id); setForm({ ...s }); };
  const cancelEdit = () => { setEditing(null); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await http.post("/admin/shows", form);
        toast.success("Show added.");
      } else {
        await http.put(`/admin/shows/${editing}`, form);
        toast.success("Show updated.");
      }
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this show?")) return;
    try {
      await http.delete(`/admin/shows/${id}`);
      toast.success("Show deleted.");
      await refresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Link to="/hologram-club" className="inline-flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/60 hover:text-[#00E5FF]" data-testid="admin-back">
            <ArrowLeft size={12} /> Back to Club
          </Link>
          <div className="flex items-center gap-4">
            <span className="font-mono-ui text-[11px] tracking-[0.22em] uppercase text-[#FF007F]">Admin · {user?.email}</span>
            <button onClick={logout} className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/60 hover:text-[#FF007F] inline-flex items-center gap-2" data-testid="admin-logout">
              <LogOut size={12} /> Log Out
            </button>
          </div>
        </div>

        <div className="mt-8">
          <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF]">Control Room</div>
          <h1 className="mt-2 font-display font-black tracking-tighter text-5xl md:text-6xl">Admin Panel.</h1>
        </div>

        {/* Stats row */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10" data-testid="admin-stats">
          {stats && Object.entries(stats).map(([k, v]) => (
            <div key={k} className="bg-[#0B0C10] p-5">
              <div className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-white/50">{k.replace(/_/g, " ")}</div>
              <div className="mt-2 font-display text-3xl font-bold text-cyan-glow">{v}</div>
            </div>
          ))}
        </div>

        {/* Live sales ticker — Stripe webhook driven */}
        <div className="mt-6">
          <LiveSalesTicker />
        </div>

        {/* Tabs */}
        <div className="mt-10 flex gap-2 font-mono-ui text-[11px] tracking-[0.22em] uppercase">
          {[{k:"shows", l:`Shows (${shows.length})`}, {k:"music", l:`Music (${shows.filter((s)=>s.is_original).length})`}, {k:"proof", l:"Proof"}, {k:"reservations", l:`Reservations (${reservations.length})`}].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`admin-tab-${t.k}`}
              className={`px-4 py-2 border transition-colors ${tab === t.k ? "bg-[#00E5FF] text-black border-[#00E5FF]" : "text-white/70 border-white/15 hover:border-white/40"}`}
            >
              {t.l}
            </button>
          ))}
          {tab === "shows" && (
            <>
              <button onClick={startNew} data-testid="admin-new-show" className="ml-auto btn-neon text-[10px] py-2 px-4 inline-flex items-center gap-2">
                <Plus size={14} /> Add Show
              </button>
              <button onClick={startNewOriginal} data-testid="admin-new-original" className="text-[10px] py-2 px-4 inline-flex items-center gap-2 border border-[#FF007F] bg-[#FF007F] text-black hover:bg-white hover:text-[#FF007F] transition-colors">
                <Sparkles size={14} /> New Original Hologram
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm("Generate AI hologram images for ALL shows? Takes ~3-5 min and uses LLM credits.")) return;
                  const ids = shows.map((s) => s.id);
                  toast.message(`Generating ${ids.length} holograms in parallel…`);
                  const results = await Promise.allSettled(
                    ids.map((id) => http.post(`/admin/shows/${id}/generate-hologram`, {}))
                  );
                  const ok = results.filter((r) => r.status === "fulfilled").length;
                  toast.success(`Generated ${ok}/${ids.length} holograms.`);
                  await refresh();
                }}
                data-testid="admin-generate-all"
                className="text-[10px] py-2 px-4 inline-flex items-center gap-2 border border-[#FF007F]/60 bg-[#FF007F]/10 text-[#FF007F] hover:bg-[#FF007F] hover:text-black transition-colors"
              >
                <Sparkles size={14} /> Generate ALL Holograms
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm(`Generate Sora 2 hologram VIDEOS for ALL ${shows.length} shows?\n\nThis runs sequentially (~3 min each, ~${Math.round(shows.length * 3)} min total) and uses Sora 2 credits via the Universal Key. You can leave this page and we'll keep working in the background.`)) return;
                  try {
                    const { data } = await http.post(`/admin/generate-all-videos`, { duration: 4, model: "sora-2-pro" });
                    toast.success(`Queued ${data.queued} video jobs (${data.skipped} already in flight). Est. total: ~${data.estimated_total_minutes} min.`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Failed to queue videos");
                  }
                }}
                data-testid="admin-generate-all-videos"
                className="text-[10px] py-2 px-4 inline-flex items-center gap-2 border border-[#FFD166]/60 bg-[#FFD166]/10 text-[#FFD166] hover:bg-[#FFD166] hover:text-black transition-colors"
              >
                <Film size={14} /> Generate ALL Videos
              </button>
              <button
                onClick={() => lipsyncBatchRef.current?.click()}
                disabled={!!lipsyncBatchId}
                data-testid="admin-lipsync-batch-btn"
                title="Upload one ZIP or multiple MP3s named like elvis.mp3 / whitney.mp3 — we lip-sync ALL artists in ~25 min"
                className="text-[10px] py-2 px-4 inline-flex items-center gap-2 border border-[#00E5FF] bg-[#00E5FF] text-black hover:bg-white transition-colors disabled:opacity-60 font-bold"
              >
                <Upload size={14} /> {lipsyncBatchId ? "Lip-syncing ALL…" : "Lip-sync ALL · Real Tracks"}
              </button>
              <button
                onClick={() => navigate("/hologram-club/admin/quick-capture")}
                data-testid="admin-quick-capture-btn"
                title="Walk-through capture every artist with Tab Audio — auto-advances when each Hedra regen completes"
                className="text-[10px] py-2 px-4 inline-flex items-center gap-2 border border-[#FF007F] bg-[#FF007F] text-black hover:bg-white transition-colors font-bold"
              >
                <Mic size={14} /> Quick Capture
              </button>
              <input
                ref={lipsyncBatchRef}
                type="file"
                multiple
                accept="audio/*,application/zip,.zip,.mp3,.wav,.m4a,.aac,.ogg,.flac"
                hidden
                data-testid="admin-lipsync-batch-input"
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  if (!files.length) return;
                  if (!window.confirm(`Lip-sync ALL matching shows from ${files.length} file(s)?\n\nThis will regenerate Hedra holograms sequentially (~2 min each). Filenames must match artist aliases (elvis, whitney, 2pac, mj, freddie, amy, prince, bowie, kairo, zade, onyx, nubia).`)) {
                    e.target.value = "";
                    return;
                  }
                  const fd = new FormData();
                  files.forEach((f) => fd.append("files", f));
                  fd.append("duration_seconds", "8");
                  fd.append("venues", "both");
                  try {
                    const { data } = await http.post(`/admin/lipsync/batch-upload`, fd, {
                      headers: { "Content-Type": "multipart/form-data" },
                    });
                    toast.success(`Matched ${data.total} shows — Hedra batch started (~${data.estimate_minutes} min).`);
                    if (data.unmatched_files?.length) {
                      toast.message(`Unmatched (skipped): ${data.unmatched_files.join(", ")}`);
                    }
                    setLipsyncBatchId(data.batch_id);
                    try { localStorage.setItem("lipsync_batch_id", data.batch_id); } catch {}
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Batch lip-sync failed");
                  } finally {
                    e.target.value = "";
                  }
                }}
              />
            </>
          )}
        </div>

        {/* Real-track lip-sync batch progress */}
        {lipsyncBatch && lipsyncBatch.status !== "complete" && (
          <div className="mt-4 border border-[#00E5FF]/40 bg-[#00E5FF]/5 p-3" data-testid="admin-lipsync-batch-status">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF] inline-flex items-center gap-2">
                <Sparkles size={12} />
                <span className="ring-dot" /> Real-track lip-sync · {lipsyncBatch.completed || 0} done · {(lipsyncBatch.total || 0) - (lipsyncBatch.completed || 0) - (lipsyncBatch.failed || 0)} pending{lipsyncBatch.failed ? ` · ${lipsyncBatch.failed} failed` : ""}
              </div>
              {lipsyncBatch.current_artist && (
                <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-white/70">
                  Now: {lipsyncBatch.current_artist}
                </div>
              )}
            </div>
            <div className="mt-2 h-1.5 bg-black/60 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#00E5FF] via-[#FF007F] to-[#FFD166] transition-all duration-500"
                style={{ width: `${Math.min(100, ((lipsyncBatch.completed || 0) / Math.max(1, lipsyncBatch.total || 1)) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Sora 2 batch-queue progress (only visible while jobs are active) */}
        {queueStatus && queueStatus.active > 0 && (
          <div className="mt-4 border border-[#FFD166]/40 bg-[#FFD166]/5 p-3" data-testid="admin-video-queue-status">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#FFD166] inline-flex items-center gap-2">
                <Film size={12} />
                <span className="ring-dot" /> Sora 2 batch · {queueStatus.done} done · {queueStatus.active} active{queueStatus.failed ? ` · ${queueStatus.failed} failed` : ""}
              </div>
              {queueStatus.current && (
                <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-white/70">
                  Now: {queueStatus.current.artist} ({queueStatus.current.status})
                </div>
              )}
            </div>
            <div className="mt-2 h-1.5 bg-black/60 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#FF007F] via-[#FFD166] to-[#00E5FF] transition-all duration-700"
                style={{ width: `${Math.min(100, Math.round((queueStatus.done / Math.max(1, queueStatus.done + queueStatus.active)) * 100))}%` }}
              />
            </div>
          </div>
        )}

        {/* Surface most-recent failure (e.g., budget exhaustion / rate limit) */}
        {queueStatus?.recent_failure?.error && queueStatus.failed > 0 && queueStatus.active === 0 && (
          <div className="mt-4 border border-[#FF007F]/50 bg-[#FF007F]/5 p-3" data-testid="admin-video-queue-error">
            <div className="flex items-center justify-between gap-3 mb-1">
              <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[#FF007F] inline-flex items-center gap-2 font-bold">
                <span className="ring-dot" /> Last Sora 2 failure ({queueStatus.recent_failure.artist})
              </div>
              <button
                onClick={async () => {
                  try {
                    const { data } = await http.post("/admin/clear-failed-jobs");
                    toast.success(`Cleared ${data.cleared} failed jobs.`);
                    // Force-refresh queue status
                    setQueueStatus((prev) => prev ? { ...prev, failed: 0, recent_failure: null } : prev);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Clear failed");
                  }
                }}
                data-testid="admin-clear-failed-btn"
                className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-white/60 hover:text-white border border-white/20 hover:border-white px-2 py-1 transition-colors"
              >
                Clear & Dismiss
              </button>
            </div>
            <div className="text-white/70 normal-case tracking-normal text-xs leading-relaxed font-mono-ui">
              {queueStatus.recent_failure.error}
            </div>
          </div>
        )}

        {/* Content */}
        {tab === "shows" ? (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-shows-list">
            {shows.map((s) => (
              <article key={s.id} className="card-surface p-5">
                <div className="flex items-start gap-4">
                  <img src={s.image} alt="" className="w-24 h-24 object-cover border border-white/10" />
                  <div className="flex-1">
                    <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF]">
                      {s.venue === "la" ? "Los Angeles" : "Las Vegas"} · {s.slot}
                    </div>
                    <div className="mt-1 font-display font-bold text-xl">{s.artist}</div>
                    <div className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-white/50">{s.era} · {s.status}</div>
                    <p className="mt-2 text-xs text-white/70 line-clamp-2">{s.tagline}</p>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <button onClick={() => startEdit(s)} data-testid={`admin-edit-${s.id}`} className="btn-ghost text-[10px] py-2 px-3 inline-flex items-center gap-2">
                    <Edit3 size={12} /> Edit
                  </button>
                  <button onClick={() => remove(s.id)} data-testid={`admin-delete-${s.id}`} className="btn-ghost text-[10px] py-2 px-3 inline-flex items-center gap-2 hover:!border-[#FF007F] hover:!text-[#FF007F]">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : tab === "music" ? (
          <MusicLibrary shows={shows} onChange={refresh} />
        ) : tab === "proof" ? (
          <ProofDashboard />
        ) : (
          <div className="mt-6 overflow-auto card-surface" data-testid="admin-reservations-list">
            <table className="w-full text-sm font-mono-ui">
              <thead className="text-left text-[10px] tracking-[0.28em] uppercase text-white/50 border-b border-white/10">
                <tr>
                  <th className="p-4">Code</th>
                  <th className="p-4">Name</th>
                  <th className="p-4">Email</th>
                  <th className="p-4">Show</th>
                  <th className="p-4">Venue</th>
                  <th className="p-4">Date</th>
                  <th className="p-4">Guests</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-4 text-[#00E5FF]">{r.confirmation_code}</td>
                    <td className="p-4 text-white">{r.name}</td>
                    <td className="p-4 text-white/70">{r.email}</td>
                    <td className="p-4 text-white/70">{r.show_id}</td>
                    <td className="p-4 text-white/70 uppercase">{r.venue}</td>
                    <td className="p-4 text-white/70">{r.date}</td>
                    <td className="p-4 text-white/70">{r.guests}</td>
                  </tr>
                ))}
                {reservations.length === 0 && (
                  <tr><td colSpan={7} className="p-6 text-center text-white/40">No reservations yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="admin-edit-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={cancelEdit} />
          <div className="relative w-full max-w-lg card-surface gradient-border p-8 max-h-[90vh] overflow-auto">
            <button onClick={cancelEdit} className="absolute top-3 right-3 text-white/60 hover:text-white" data-testid="admin-edit-close">
              <X size={20} />
            </button>
            <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[#00E5FF]">
              {editing === "new" ? "Add" : "Edit"} / Show
            </div>
            <h3 className="mt-2 font-display font-black tracking-tighter text-3xl">
              {editing === "new" ? "New Transmission." : "Rewire Transmission."}
            </h3>

            <div className="mt-6 space-y-4">
              <AdminField label="Artist" value={form.artist} onChange={(v) => setForm({...form, artist: v})} testId="admin-form-artist" />
              <div className="grid grid-cols-2 gap-3">
                <AdminField label="Era" value={form.era} onChange={(v) => setForm({...form, era: v})} testId="admin-form-era" />
                <AdminField label="Slot" value={form.slot} onChange={(v) => setForm({...form, slot: v})} testId="admin-form-slot" />
              </div>
              <AdminField label="Start (ISO)" value={form.start_iso} onChange={(v) => setForm({...form, start_iso: v})} testId="admin-form-start" />
              <AdminField label="Cover Image URL" value={form.image} onChange={(v) => setForm({...form, image: v})} testId="admin-form-image" />
              <div>
                <label className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/50">Tagline</label>
                <textarea
                  value={form.tagline}
                  onChange={(e) => setForm({...form, tagline: e.target.value})}
                  rows={2}
                  data-testid="admin-form-tagline"
                  className="mt-2 w-full bg-[#050505] border border-white/15 text-white p-3 font-body text-sm focus:border-[#00E5FF] focus:outline-none"
                />
              </div>
              <div>
                <label className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/50">Character Bio</label>
                <textarea
                  value={form.bio || ""}
                  onChange={(e) => setForm({...form, bio: e.target.value})}
                  rows={3}
                  data-testid="admin-form-bio"
                  placeholder="Backstory of your original hologram character…"
                  className="mt-2 w-full bg-[#050505] border border-white/15 text-white p-3 font-body text-sm focus:border-[#00E5FF] focus:outline-none"
                />
              </div>
              {/* Original character toggle — flips to a custom-character creation flow */}
              <label className="flex items-start gap-3 cursor-pointer p-3 border border-[#FF007F]/30 bg-[#FF007F]/5 hover:bg-[#FF007F]/10 transition-colors" data-testid="admin-form-original-row">
                <input
                  type="checkbox"
                  checked={!!form.is_original}
                  onChange={(e) => setForm({ ...form, is_original: e.target.checked })}
                  data-testid="admin-form-is-original"
                  className="mt-1 accent-[#FF007F] cursor-pointer"
                />
                <div className="flex-1">
                  <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#FF007F]">
                    Original Character
                  </div>
                  <div className="mt-0.5 font-body text-xs text-white/70">
                    Mine — not a real-celebrity cover. Uses the description below for AI generation.
                  </div>
                </div>
              </label>

              {form.is_original && (
                <>
                  <div>
                    <label className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/50">Character Description <span className="text-[#FF007F]">·</span> what the hologram looks like</label>
                    <textarea
                      value={form.character_description || ""}
                      onChange={(e) => setForm({ ...form, character_description: e.target.value })}
                      rows={4}
                      data-testid="admin-form-character-description"
                      placeholder='e.g., "Cyborg female DJ with neon-blue dreadlocks, glowing arm tattoos, mirrored visor, latex bodysuit, levitating above an LED platform."'
                      className="mt-2 w-full bg-[#050505] border border-[#FF007F]/40 text-white p-3 font-body text-sm focus:border-[#FF007F] focus:outline-none"
                    />
                    <div className="mt-1 font-mono-ui text-[9px] tracking-[0.18em] uppercase text-white/40">
                      Used as the AI prompt seed when you click <span className="text-[#FF007F]">AI Generate</span> / <span className="text-[#FF007F]">AI Video</span>.
                    </div>
                  </div>
                  <AdminField label="Genre / Vibe" value={form.genre || ""} onChange={(v) => setForm({ ...form, genre: v })} testId="admin-form-genre" placeholder="Synthwave · Trap · Acid Jazz · Ambient · Future Soul…" />
                </>
              )}

              <div className="grid grid-cols-3 gap-3">
                <AdminSelect label="Venue" value={form.venue} onChange={(v) => setForm({...form, venue: v})} options={[{v:"vegas",l:"Las Vegas"},{v:"la",l:"Los Angeles"}]} testId="admin-form-venue" />
                <AdminSelect label="Status" value={form.status} onChange={(v) => setForm({...form, status: v})} options={[{v:"live",l:"Live"},{v:"upcoming",l:"Upcoming"},{v:"soon",l:"Soon"}]} testId="admin-form-status" />
                <AdminField label="PPV Price ($)" value={form.ppv_price_usd ?? 0} onChange={(v) => setForm({...form, ppv_price_usd: parseFloat(v) || 0})} testId="admin-form-ppv" />
              </div>

              {editing !== "new" && (
                <UploadRow showId={editing} form={form} onUpdated={(patch) => setForm({...form, ...patch})} />
              )}
            </div>

            <button onClick={save} disabled={saving} className="btn-neon w-full mt-8 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="admin-form-save">
              <Save size={14} /> {saving ? "Saving..." : editing === "new" ? "Create Show" : "Update Show"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminField({ label, value, onChange, testId, placeholder }) {
  return (
    <div>
      <label className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/50">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-2 w-full bg-[#050505] border border-white/15 text-white p-3 font-mono-ui text-sm focus:border-[#00E5FF] focus:outline-none"
      />
    </div>
  );
}

function AdminSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/50">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-2 w-full bg-[#050505] border border-white/15 text-white p-3 font-mono-ui text-sm focus:border-[#00E5FF] focus:outline-none"
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

// =================================================================
// PROOF DASHBOARD — single-page audit of every AI asset generated
// =================================================================
function ProofDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/proof-dashboard");
      setData(data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  if (loading && !data) {
    return <div className="mt-6 card-surface p-12 text-center font-mono-ui text-xs tracking-[0.28em] uppercase text-white/50">Auditing assets…</div>;
  }
  if (error) {
    return <div className="mt-6 card-surface p-6 border border-[#FF007F]/40 font-mono-ui text-xs text-[#FF007F]">{error}</div>;
  }
  if (!data) return null;

  const { counts, rows } = data;
  const successRate = (counts.ai_videos_done + counts.ai_videos_failed) > 0
    ? Math.round(counts.ai_videos_done / (counts.ai_videos_done + counts.ai_videos_failed) * 100)
    : 0;

  const stats = [
    { l: "AI Hologram Images", v: counts.ai_holograms, sub: `of ${counts.shows_total} shows`, c: "#00E5FF", k: "ai_holograms" },
    { l: "Sora 2 Videos · Done", v: counts.ai_videos_done, sub: `${counts.ai_videos_failed} failed (${successRate}% success)`, c: "#FFD166", k: "ai_videos" },
    { l: "MP3 Tracks Uploaded", v: counts.mp3_uploaded, sub: `${counts.shows_total - counts.mp3_uploaded} shows still silent`, c: "#FF007F", k: "mp3" },
    { l: "Original Characters", v: counts.originals, sub: "100% your IP", c: "#FFD166", k: "originals" },
  ];

  return (
    <div className="mt-6" data-testid="admin-proof-dashboard">
      <div className="card-surface p-6 md:p-8 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF]">Receipts · Live Audit</div>
            <div className="mt-2 font-display font-bold text-3xl">What your credits actually built.</div>
            <div className="mt-2 font-body text-sm text-white/60">Every hologram, every Sora 2 clip, every MP3 — verified live on disk and in the database.</div>
          </div>
          <button onClick={reload} className="btn-ghost text-[10px] py-2 px-4 inline-flex items-center gap-2" data-testid="proof-refresh">
            Refresh
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {stats.map((s) => (
            <div key={s.k} className="border border-white/10 bg-[#050505] p-4" data-testid={`proof-stat-${s.k}`}>
              <div className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-white/50">{s.l}</div>
              <div className="mt-2 font-display font-black text-5xl tracking-tight" style={{ color: s.c }}>{s.v}</div>
              <div className="mt-1 font-mono-ui text-[9px] tracking-[0.18em] uppercase text-white/40">{s.sub}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3" data-testid="proof-rows">
        {rows.map((r) => <ProofRow key={r.id} row={r} />)}
      </div>
    </div>
  );
}

function ProofRow({ row: r }) {
  const [open, setOpen] = useState(false);
  const charSrc = r.character_image_url
    ? (r.character_image_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${r.character_image_url}` : r.character_image_url)
    : null;
  const audSrc = r.audio_url
    ? (r.audio_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${r.audio_url}` : r.audio_url)
    : null;
  const trailerSrc = r.trailer_url
    ? (r.trailer_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${r.trailer_url}` : r.trailer_url)
    : null;
  const isAiTrailer = trailerSrc && trailerSrc.includes("/trailer_ai_");
  const isStockTrailer = trailerSrc && trailerSrc.includes("/_trailers/");

  return (
    <div
      className={`border ${r.is_original ? "border-[#FFD166]/40 bg-[#FFD166]/5" : "border-white/10 bg-[#0B0C10]"}`}
      data-testid={`proof-row-${r.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full p-4 md:p-5 text-left flex items-start gap-4 hover:bg-white/5 transition-colors"
      >
        {charSrc ? (
          <img src={charSrc} alt={r.artist} className="w-20 h-20 object-cover border border-white/10 flex-shrink-0" />
        ) : (
          <div className="w-20 h-20 bg-white/5 border border-white/10 flex items-center justify-center font-mono-ui text-[8px] text-white/30 flex-shrink-0">NO IMG</div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-black text-2xl tracking-tight">{r.artist}</span>
            {r.is_original && (
              <span className="inline-flex items-center gap-1 border border-[#FFD166] bg-[#FFD166] text-black px-1.5 py-0.5 font-mono-ui text-[8px] tracking-[0.28em] uppercase font-bold">
                Original
              </span>
            )}
            <span className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-white/40">{r.venue === "la" ? "LA" : "VEG"}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge ok={!!r.character_image_url} label="Hologram" testId={`proof-${r.id}-hologram`} />
            <Badge ok={!!r.audio_url} label={r.audio_url ? "MP3 Live" : "No MP3"} testId={`proof-${r.id}-mp3`} negativeColor="#FF007F" />
            <Badge
              ok={isAiTrailer}
              label={isAiTrailer ? "Sora 2 video" : isStockTrailer ? "Stock trailer" : "No trailer"}
              testId={`proof-${r.id}-trailer`}
              negativeColor="#FFD166"
            />
            {r.video_jobs_done > 0 && <Badge ok={true} label={`${r.video_jobs_done} Sora done`} testId={`proof-${r.id}-soradone`} />}
            {r.video_jobs_failed > 0 && <Badge ok={false} label={`${r.video_jobs_failed} Sora failed`} testId={`proof-${r.id}-sorafail`} negativeColor="#FF007F" />}
          </div>
        </div>
        <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-white/40 self-center">
          {open ? "Hide" : "Show"}
        </div>
      </button>

      {open && (
        <div className="border-t border-white/10 p-4 md:p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-white/50 mb-2">Hologram Image</div>
            {charSrc ? (
              <a href={charSrc} target="_blank" rel="noopener noreferrer">
                <img src={charSrc} alt={r.artist} className="w-full aspect-[3/4] object-cover border border-white/10" />
              </a>
            ) : (
              <div className="w-full aspect-[3/4] bg-white/5 border border-white/10 flex items-center justify-center font-mono-ui text-[10px] text-white/30">NOT GENERATED</div>
            )}
            {charSrc && <div className="mt-1 font-mono-ui text-[9px] text-white/30 break-all">{r.character_image_url}</div>}
          </div>

          <div>
            <div className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-white/50 mb-2">Trailer · {isAiTrailer ? "AI Sora 2" : isStockTrailer ? "stock" : "none"}</div>
            {trailerSrc ? (
              <video src={trailerSrc} controls muted loop className="w-full aspect-[3/4] object-cover border border-white/10 bg-black" data-testid={`proof-${r.id}-trailer-video`} />
            ) : (
              <div className="w-full aspect-[3/4] bg-white/5 border border-white/10 flex items-center justify-center font-mono-ui text-[10px] text-white/30">NO TRAILER</div>
            )}
            {trailerSrc && <div className="mt-1 font-mono-ui text-[9px] text-white/30 break-all">{r.trailer_url}</div>}
          </div>

          <div>
            <div className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-white/50 mb-2">MP3 Track</div>
            {audSrc ? (
              <>
                <audio src={audSrc} controls className="w-full" data-testid={`proof-${r.id}-audio`} />
                <div className="mt-2 font-mono-ui text-[9px] text-white/30 break-all">{r.audio_url}</div>
              </>
            ) : (
              <div className="w-full p-6 bg-[#FF007F]/10 border border-[#FF007F]/40 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[#FF007F] text-center">
                No MP3 yet —<br />upload via Music tab
              </div>
            )}
            {r.latest_video_job && r.latest_video_job.status === "failed" && (
              <div className="mt-3 p-2 border border-[#FF007F]/30 bg-[#FF007F]/5 font-mono-ui text-[9px] text-[#FF007F]">
                <div className="font-bold tracking-[0.22em] uppercase">Latest Sora failure:</div>
                <div className="mt-1 normal-case tracking-normal text-white/60">{r.latest_video_job.error?.slice(0, 200)}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Badge({ ok, label, testId, negativeColor = "#666" }) {
  return (
    <span
      className="inline-flex items-center gap-1 border px-2 py-0.5 font-mono-ui text-[9px] tracking-[0.22em] uppercase"
      style={{
        borderColor: ok ? "rgba(0,229,255,0.45)" : `${negativeColor}66`,
        background: ok ? "rgba(0,229,255,0.1)" : "transparent",
        color: ok ? "#00E5FF" : negativeColor,
      }}
      data-testid={testId}
    >
      <span className="ring-dot" style={{
        width: 4, height: 4,
        background: ok ? "#00E5FF" : negativeColor,
        boxShadow: ok ? "0 0 6px #00E5FF" : "none",
      }} />
      {label}
    </span>
  );
}

// =================================================================
// MUSIC LIBRARY — single-page batch MP3 uploader for original artists
// =================================================================
function MusicLibrary({ shows, onChange }) {
  const originals = shows.filter((s) => s.is_original);

  if (originals.length === 0) {
    return (
      <div className="mt-6 card-surface p-12 text-center" data-testid="admin-music-empty">
        <Music size={48} className="mx-auto text-[#FF007F] opacity-50" />
        <div className="mt-4 font-display font-bold text-2xl">No original artists yet.</div>
        <div className="mt-2 font-body text-sm text-white/60 max-w-md mx-auto">
          Create an original hologram first (Shows tab → "New Original Hologram"), then come back here to upload their MP3 tracks.
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6" data-testid="admin-music-library">
      <div className="card-surface p-6 md:p-8 mb-6 border border-[#FF007F]/30">
        <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#FF007F] inline-flex items-center gap-2">
          <Music size={12} /> Your Music Library
        </div>
        <div className="mt-2 font-display font-bold text-3xl">Upload your original tracks.</div>
        <div className="mt-2 font-body text-sm text-white/60 max-w-2xl">
          One row per original artist. Drag & drop or click "Upload MP3" — the track is published immediately and appears on the Originals Showcase as <span className="text-[#00E5FF] font-bold">MP3 Ready</span>.
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3" data-testid="admin-music-rows">
        {originals.map((s) => (
          <MusicRow key={s.id} show={s} onChange={onChange} />
        ))}
      </div>
    </div>
  );
}

function MusicRow({ show, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const audSrc = show.audio_url
    ? (show.audio_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${show.audio_url}` : show.audio_url)
    : null;
  const charSrc = show.character_image_url
    ? (show.character_image_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${show.character_image_url}` : show.character_image_url)
    : show.image;

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File is over 50 MB. Compress and try again.");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("audio", file);
    try {
      await http.post(`/admin/shows/${show.id}/audio`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${show.artist}: ${file.name} uploaded.`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      className={`flex items-center gap-4 md:gap-6 p-4 md:p-5 border ${
        drag ? "border-[#FF007F] bg-[#FF007F]/15"
             : audSrc ? "border-[#00E5FF]/30 bg-[#00E5FF]/5"
             : "border-[#FF007F]/30 bg-[#FF007F]/5"
      } transition-colors`}
      data-testid={`music-row-${show.id}`}
    >
      <img src={charSrc} alt={show.artist} className="w-20 h-20 md:w-24 md:h-24 object-cover border border-white/10 flex-shrink-0" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display font-black tracking-tight text-2xl">{show.artist}</div>
          {show.genre && (
            <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-white/60 border border-white/15 px-2 py-0.5">
              {show.genre}
            </span>
          )}
          {audSrc ? (
            <span className="inline-flex items-center gap-1 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[#00E5FF]">
              <span className="ring-dot" style={{ width: 5, height: 5, background: "#00E5FF", boxShadow: "0 0 8px #00E5FF" }} />
              MP3 Live
            </span>
          ) : (
            <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[#FF007F]">No track yet</span>
          )}
        </div>
        <div className="mt-1 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-white/50">
          {show.venue === "la" ? "Los Angeles" : "Las Vegas"} · {show.slot} · ${(show.ppv_price_usd || 0).toFixed(2)}
        </div>
        {audSrc && (
          <audio src={audSrc} controls className="mt-2 w-full max-w-md" data-testid={`music-row-audio-${show.id}`} />
        )}
      </div>

      <div className="flex-shrink-0">
        <input
          ref={fileRef}
          type="file"
          accept="audio/mpeg,audio/mp3,audio/wav,audio/aac,audio/mp4,audio/x-m4a"
          hidden
          data-testid={`music-row-file-${show.id}`}
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid={`music-row-upload-${show.id}`}
          className={`text-[10px] tracking-[0.28em] uppercase font-bold py-3 px-5 inline-flex items-center gap-2 disabled:opacity-60 transition-colors ${
            audSrc
              ? "btn-ghost"
              : "bg-[#FF007F] text-black hover:bg-white border border-[#FF007F]"
          }`}
        >
          <Upload size={12} /> {busy ? "Uploading…" : audSrc ? "Replace" : "Upload MP3"}
        </button>
      </div>
    </div>
  );
}

function UploadRow({ showId, form, onUpdated }) {
  const imgRef = useRef(null);
  const audRef = useRef(null);
  const vidRef = useRef(null);
  const [busyImg, setBusyImg] = useState(false);
  const [busyAud, setBusyAud] = useState(false);
  const [busyVid, setBusyVid] = useState(false);
  const [trailerUrlDraft, setTrailerUrlDraft] = useState(form.trailer_url || "");
  const [savingUrl, setSavingUrl] = useState(false);
  const [youtubeUrlDraft, setYoutubeUrlDraft] = useState(form.youtube_url || "");
  const [savingYt, setSavingYt] = useState(false);
  const [setlistDraft, setSetlistDraft] = useState((form.setlist || []).join("\n"));
  const [savingSetlist, setSavingSetlist] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [variation, setVariation] = useState("");
  const [showVariation, setShowVariation] = useState(false);
  const [videoJob, setVideoJob] = useState(null); // {id, status, error, started_at}
  const [videoDuration, setVideoDuration] = useState(4);
  const [videoModel, setVideoModel] = useState("sora-2-pro");
  const [videoElapsed, setVideoElapsed] = useState(0);

  useEffect(() => { setTrailerUrlDraft(form.trailer_url || ""); }, [form.trailer_url]);
  useEffect(() => { setYoutubeUrlDraft(form.youtube_url || ""); }, [form.youtube_url]);
  useEffect(() => { setSetlistDraft((form.setlist || []).join("\n")); }, [form.setlist]);

  // Resume polling on mount if a job was already in flight for this show
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await http.get(`/admin/shows/${showId}/latest-video-job`);
        if (!alive || !data?.id) return;
        if (data.status === "pending" || data.status === "running") {
          setVideoJob({ ...data, started_at: Date.parse(data.created_at) || Date.now() });
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [showId]);

  // Poll while a video job is running
  useEffect(() => {
    if (!videoJob || (videoJob.status !== "pending" && videoJob.status !== "running")) return;
    const startedAt = videoJob.started_at || Date.now();
    const tick = setInterval(() => {
      setVideoElapsed(Math.round((Date.now() - startedAt) / 1000));
    }, 1000);
    const poll = setInterval(async () => {
      try {
        const { data } = await http.get(`/admin/video-jobs/${videoJob.id}`);
        if (data.status === "done") {
          onUpdated({ trailer_url: data.trailer_url });
          toast.success("Hologram video ready.");
          setVideoJob(null);
          clearInterval(tick); clearInterval(poll);
        } else if (data.status === "failed") {
          toast.error(`Video generation failed: ${data.error || "unknown"}`);
          setVideoJob({ ...videoJob, status: "failed", error: data.error });
          clearInterval(tick); clearInterval(poll);
        } else {
          // Keep status fresh (running/pending)
          setVideoJob((prev) => (prev ? { ...prev, status: data.status } : prev));
        }
      } catch (e) {
        // network blip — keep polling
      }
    }, 5000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [videoJob?.id, videoJob?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateHologram = async () => {
    setGenBusy(true);
    try {
      const { data } = await http.post(`/admin/shows/${showId}/generate-hologram`, { variation: variation.trim() || undefined });
      onUpdated({ character_image_url: data.character_image_url, is_original: true });
      toast.success(form.character_image_url ? "New variation generated." : "AI hologram generated.");
      setVariation("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenBusy(false);
    }
  };

  const generateVideo = async () => {
    try {
      // Portrait 1024x1792 is sora-2-pro only; sora-2 falls back to landscape 1280x720.
      const size = videoModel === "sora-2-pro" ? "1024x1792" : "1280x720";
      const { data } = await http.post(`/admin/shows/${showId}/generate-hologram-video`, {
        duration: videoDuration,
        model: videoModel,
        size,
        variation: variation.trim() || undefined,
      });
      toast.message(`Sora 2 generation started — est. ${Math.round(data.estimated_seconds / 60)} min. You can leave this page; we'll keep working in the background.`);
      setVideoJob({ id: data.job_id, status: "pending", started_at: Date.now() });
      setVideoElapsed(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start video generation");
    }
  };

  // === HEDRA — true AI lip-synced singing hologram via Character-3 ===
  const generateHedraHologram = async () => {
    if (!form.character_image_url || !form.audio_url) {
      toast.error("Upload the character image AND audio (MP3) first.");
      return;
    }
    try {
      const { data } = await http.post(`/admin/shows/${showId}/generate-hedra-hologram`, {
        duration: 10,
      });
      toast.message(`Hedra lip-sync started — est. 90s. Page will auto-update when done.`);
      setVideoJob({ id: data.job_id, status: "pending", started_at: Date.now(), provider: "hedra" });
      setVideoElapsed(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start Hedra generation");
    }
  };

  // === LIP-SYNC to the REAL song — upload a snippet of the actual track ===
  const lipsyncRef = useRef(null);
  const [lipsyncBusy, setLipsyncBusy] = useState(false);
  const [tabAudioOpen, setTabAudioOpen] = useState(false);
  const lipsyncFromUpload = async (file) => {
    if (!file) return;
    if (!form.character_image_url) {
      toast.error("Upload the character image first.");
      return;
    }
    setLipsyncBusy(true);
    const fd = new FormData();
    fd.append("audio", file);
    fd.append("duration_seconds", "8");
    try {
      const { data } = await http.post(
        `/admin/shows/${showId}/lipsync-from-upload`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`Lip-sync started using "${file.name}" — Hedra will mouth-match the real song (~90s).`);
      setVideoJob({ id: data.job_id, status: "pending", started_at: Date.now(), provider: "hedra" });
      setVideoElapsed(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lip-sync upload failed");
    } finally {
      setLipsyncBusy(false);
      if (lipsyncRef.current) lipsyncRef.current.value = "";
    }
  };

  const lipsyncFromYoutube = async () => {
    if (!form.character_image_url) {
      toast.error("Upload the character image first.");
      return;
    }
    const yt = (form.youtube_url || "").trim() || (window.prompt("Paste a YouTube URL to lip-sync to:") || "").trim();
    if (!yt) return;
    const startStr = window.prompt("Start at (seconds into the song)?", "30") || "30";
    const start_seconds = Math.max(0, Math.min(600, parseInt(startStr, 10) || 30));
    setLipsyncBusy(true);
    try {
      const { data } = await http.post(`/admin/shows/${showId}/lipsync-from-youtube`, {
        youtube_url: yt,
        start_seconds,
        duration_seconds: 8,
      });
      toast.success(`YouTube lip-sync started — Hedra will mouth-match the real song (~90s).`);
      setVideoJob({ id: data.job_id, status: "pending", started_at: Date.now(), provider: "hedra" });
      setVideoElapsed(0);
    } catch (e) {
      const detail = e?.response?.data?.detail || "YouTube fetch failed";
      toast.error(`${detail}. Try the file upload button instead.`);
    } finally {
      setLipsyncBusy(false);
    }
  };

  const upload = async (file, kind) => {
    if (!file) return;
    const map = { character: setBusyImg, audio: setBusyAud, trailer: setBusyVid };
    const setBusy = map[kind];
    setBusy(true);
    const fd = new FormData();
    const fieldName = kind === "character" ? "image" : kind === "audio" ? "audio" : "video";
    fd.append(fieldName, file);
    try {
      const { data } = await http.post(`/admin/shows/${showId}/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (kind === "character") onUpdated({ character_image_url: data.character_image_url });
      else if (kind === "audio") onUpdated({ audio_url: data.audio_url });
      else onUpdated({ trailer_url: data.trailer_url });
      toast.success(`${kind === "trailer" ? "Trailer" : kind === "character" ? "Character image" : "Audio"} uploaded.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const saveTrailerUrl = async () => {
    setSavingUrl(true);
    try {
      const { data } = await http.post(`/admin/shows/${showId}/trailer-url`, { trailer_url: trailerUrlDraft });
      onUpdated({ trailer_url: data.trailer_url });
      toast.success("Trailer URL saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingUrl(false);
    }
  };

  const saveYoutubeUrl = async () => {
    setSavingYt(true);
    try {
      const { data } = await http.post(`/admin/shows/${showId}/youtube-url`, { youtube_url: youtubeUrlDraft });
      onUpdated({ youtube_url: data.youtube_url });
      toast.success("YouTube URL saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingYt(false);
    }
  };

  const saveSetlist = async () => {
    setSavingSetlist(true);
    try {
      const list = setlistDraft
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      const { data } = await http.post(`/admin/shows/${showId}/setlist`, { setlist: list });
      onUpdated({ setlist: data.setlist });
      toast.success(`Setlist saved (${(data.setlist || []).length} tracks).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingSetlist(false);
    }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;
  const charPreview = form.character_image_url ? (form.character_image_url.startsWith("/api") ? `${backend}${form.character_image_url}` : form.character_image_url) : null;
  const audSrc = form.audio_url ? (form.audio_url.startsWith("/api") ? `${backend}${form.audio_url}` : form.audio_url) : null;
  const vidSrc = form.trailer_url ? (form.trailer_url.startsWith("/api") ? `${backend}${form.trailer_url}` : form.trailer_url) : null;

  return (
    <div className="border border-[#FFD166]/25 bg-[#FFD166]/5 p-4">
      <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#FFD166]">Original Hologram Assets</div>
      <p className="text-xs text-white/60 mt-1">Upload an exclusive character image, original MP3, and a real-time trailer (MP4) for this artist.</p>

      <div className="mt-4 grid grid-cols-1 gap-3">
        <div className="border border-white/10 bg-[#0B0C10] p-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-white/50 inline-flex items-center gap-2"><ImageIcon size={12} /> Hologram</span>
            <div className="flex items-center gap-1 flex-wrap">
              <button type="button" onClick={generateHologram} disabled={genBusy} className="text-[10px] py-1.5 px-3 inline-flex items-center gap-1 disabled:opacity-60 border border-[#FF007F]/60 bg-[#FF007F]/10 text-[#FF007F] hover:bg-[#FF007F] hover:text-black transition-colors" data-testid={form.character_image_url ? "admin-regenerate-hologram-btn" : "admin-generate-hologram-btn"} title="Generate via Nano Banana — re-roll for a new variation">
                <Sparkles size={12} /> {genBusy ? "Generating…" : (form.character_image_url ? "Regenerate" : "AI Generate")}
              </button>
              <button type="button" onClick={() => setShowVariation((v) => !v)} className="btn-ghost text-[10px] py-1.5 px-2 inline-flex items-center gap-1" data-testid="admin-toggle-variation" title="Add a custom prompt twist for the next variation">
                <Edit3 size={12} />
              </button>
              <button type="button" onClick={() => imgRef.current?.click()} disabled={busyImg} className="btn-ghost text-[10px] py-1.5 px-3 inline-flex items-center gap-1 disabled:opacity-60" data-testid="admin-upload-char-btn">
                <Upload size={12} /> {busyImg ? "..." : "Upload"}
              </button>
              <input ref={imgRef} type="file" accept="image/*" hidden data-testid="admin-upload-char" onChange={(e) => upload(e.target.files?.[0], "character")} />
            </div>
          </div>
          {showVariation && (
            <input
              value={variation}
              onChange={(e) => setVariation(e.target.value)}
              placeholder='Optional prompt tweak — e.g., "1956 Sun Records leather era, sepia accent"'
              data-testid="admin-variation-input"
              className="mt-2 w-full bg-[#050505] border border-[#FF007F]/30 text-white p-2 font-mono-ui text-[10px] focus:border-[#FF007F] focus:outline-none"
            />
          )}
          {charPreview && <img src={charPreview} alt="char" className="mt-3 w-full aspect-square object-cover border border-white/10" data-testid="admin-char-preview" />}
        </div>

        <div className={`border-2 ${form.is_original && !audSrc ? "border-[#FF007F] bg-[#FF007F]/10" : audSrc ? "border-[#00E5FF]/50 bg-[#00E5FF]/5" : "border-white/10 bg-[#0B0C10]"} p-4 transition-colors`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase inline-flex items-center gap-2 text-white/80">
              <Music size={14} className={form.is_original ? "text-[#FF007F]" : ""} />
              {form.is_original ? "Your Original MP3 Track" : "Audio Track"}
              {audSrc && <span className="text-[#00E5FF] ml-1">· LIVE</span>}
            </span>
            <button
              type="button"
              onClick={() => audRef.current?.click()}
              disabled={busyAud}
              className={`text-[10px] py-2 px-4 inline-flex items-center gap-1 disabled:opacity-60 transition-colors ${form.is_original && !audSrc ? "bg-[#FF007F] text-black hover:bg-white" : "btn-ghost"}`}
              data-testid="admin-upload-audio-btn"
            >
              <Upload size={12} /> {busyAud ? "Uploading…" : audSrc ? "Replace MP3" : "Upload MP3"}
            </button>
            <input ref={audRef} type="file" accept="audio/mpeg,audio/mp3,audio/wav,audio/aac,audio/mp4,audio/x-m4a" hidden data-testid="admin-upload-audio" onChange={(e) => upload(e.target.files?.[0], "audio")} />
          </div>
          {form.is_original && !audSrc && (
            <div className="mt-2 font-body text-xs text-white/60 leading-relaxed">
              Drop in <span className="text-[#FF007F] font-bold">your original track</span> for this hologram. Up to 50 MB · MP3, WAV, AAC, M4A. Listeners will stream this with their PPV purchase or VIP/Resident membership.
            </div>
          )}
          {audSrc && (
            <>
              <audio src={audSrc} controls className="mt-3 w-full" data-testid="admin-audio-preview" />
              <div className="mt-2 font-mono-ui text-[9px] tracking-[0.22em] uppercase text-white/40 break-all">
                {form.audio_url}
              </div>
            </>
          )}
        </div>

        <div className="border border-white/10 bg-[#0B0C10] p-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-white/50 inline-flex items-center gap-2"><Video size={12} /> Trailer</span>
            <div className="flex items-center gap-1 flex-wrap">
              <button
                type="button"
                onClick={generateHedraHologram}
                disabled={!!videoJob || !form.character_image_url || !form.audio_url}
                className="text-[10px] py-1.5 px-3 inline-flex items-center gap-1 disabled:opacity-60 border border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF] hover:bg-[#00E5FF] hover:text-black transition-colors font-bold"
                data-testid="admin-generate-hedra-btn"
                title="Hedra Character-3 — REAL AI lip-synced singing hologram (~90s)"
              >
                <Film size={12} /> {videoJob?.provider === "hedra" ? "Hedra…" : "🎤 Hedra Lip-Sync"}
              </button>
              <button
                type="button"
                onClick={() => lipsyncRef.current?.click()}
                disabled={!!videoJob || lipsyncBusy || !form.character_image_url}
                className="text-[10px] py-1.5 px-3 inline-flex items-center gap-1 disabled:opacity-60 border border-[#FFD166] bg-[#FFD166]/10 text-[#FFD166] hover:bg-[#FFD166] hover:text-black transition-colors font-bold"
                data-testid="admin-lipsync-upload-btn"
                title="Upload a 4-30s MP3 clip of the REAL song — Hedra will mouth-match it"
              >
                <Upload size={12} /> {lipsyncBusy ? "Syncing…" : "Lip-sync · MP3"}
              </button>
              <input
                ref={lipsyncRef}
                type="file"
                accept="audio/*"
                hidden
                data-testid="admin-lipsync-upload"
                onChange={(e) => lipsyncFromUpload(e.target.files?.[0])}
              />
              <button
                type="button"
                onClick={lipsyncFromYoutube}
                disabled={!!videoJob || lipsyncBusy || !form.character_image_url}
                className="text-[10px] py-1.5 px-3 inline-flex items-center gap-1 disabled:opacity-60 border border-[#FF0000]/60 bg-[#FF0000]/10 text-[#FF0000] hover:bg-[#FF0000] hover:text-white transition-colors"
                data-testid="admin-lipsync-youtube-btn"
                title="Best-effort: fetch a snippet from YouTube and lip-sync (may fail from server IPs — use MP3 upload if it does)"
              >
                <Youtube size={12} /> {lipsyncBusy ? "…" : "Lip-sync · YT"}
              </button>
              <button
                type="button"
                onClick={() => setTabAudioOpen(true)}
                disabled={!!videoJob || !form.character_image_url}
                className="text-[10px] py-1.5 px-3 inline-flex items-center gap-1 disabled:opacity-60 border border-[#FF007F] bg-[#FF007F]/10 text-[#FF007F] hover:bg-[#FF007F] hover:text-black transition-colors font-bold"
                data-testid="admin-lipsync-tab-btn"
                title="Record audio from a YouTube/Spotify tab in Chrome — bypasses YouTube IP block. Mouth will mouth-match the real song."
              >
                <Mic size={12} /> Lip-sync · Tab
              </button>
              <button
                type="button"
                onClick={generateVideo}
                disabled={!!videoJob}
                className="text-[10px] py-1.5 px-3 inline-flex items-center gap-1 disabled:opacity-60 border border-[#FF007F]/60 bg-[#FF007F]/10 text-[#FF007F] hover:bg-[#FF007F] hover:text-black transition-colors"
                data-testid="admin-generate-video-btn"
                title="Generate AI hologram VIDEO via Sora 2 (~3 min)"
              >
                <Film size={12} /> {videoJob && videoJob.provider !== "hedra" ? "Generating…" : "Sora 2"}
              </button>
              <button type="button" onClick={() => vidRef.current?.click()} disabled={busyVid} className="btn-ghost text-[10px] py-1.5 px-3 inline-flex items-center gap-1 disabled:opacity-60" data-testid="admin-upload-trailer-btn">
                <Upload size={12} /> {busyVid ? "..." : "Upload"}
              </button>
              <input ref={vidRef} type="file" accept="video/*" hidden data-testid="admin-upload-trailer" onChange={(e) => upload(e.target.files?.[0], "trailer")} />
            </div>
          </div>
          {videoJob && (
            <div className="mt-2 border border-[#FF007F]/30 bg-[#FF007F]/5 p-2 font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[#FF007F]" data-testid="admin-video-job-status">
              <div className="flex items-center gap-2">
                <span className="ring-dot" />
                <span>Sora 2 · {videoJob.status} · {videoElapsed}s elapsed</span>
              </div>
              {videoJob.status === "failed" && videoJob.error && (
                <div className="mt-1 text-white/60 normal-case tracking-normal">{videoJob.error}</div>
              )}
            </div>
          )}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <select
              value={videoDuration}
              onChange={(e) => setVideoDuration(parseInt(e.target.value, 10))}
              data-testid="admin-video-duration"
              className="bg-[#050505] border border-white/15 text-white p-1.5 font-mono-ui text-[10px] focus:border-[#FF007F] focus:outline-none"
            >
              <option value={4}>4s · ~3 min</option>
              <option value={8}>8s · ~5 min</option>
              <option value={12}>12s · ~8 min</option>
            </select>
            <select
              value={videoModel}
              onChange={(e) => setVideoModel(e.target.value)}
              data-testid="admin-video-model"
              className="bg-[#050505] border border-white/15 text-white p-1.5 font-mono-ui text-[10px] focus:border-[#FF007F] focus:outline-none"
            >
              <option value="sora-2-pro">sora-2-pro · portrait</option>
              <option value="sora-2">sora-2 · landscape</option>
            </select>
          </div>
          {vidSrc && <video src={vidSrc} controls muted className="mt-3 w-full aspect-video object-cover border border-white/10" data-testid="admin-trailer-preview" />}
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <input
          value={trailerUrlDraft}
          onChange={(e) => setTrailerUrlDraft(e.target.value)}
          placeholder="…or paste a trailer URL (https://… .mp4)"
          data-testid="admin-trailer-url"
          className="flex-1 bg-[#050505] border border-white/15 text-white p-3 font-mono-ui text-xs focus:border-[#00E5FF] focus:outline-none"
        />
        <button type="button" onClick={saveTrailerUrl} disabled={savingUrl} className="btn-ghost text-[10px] px-4 py-2 disabled:opacity-60" data-testid="admin-trailer-url-save">
          {savingUrl ? "..." : "Save URL"}
        </button>
      </div>

      <div className="mt-3 border border-[#FF0000]/40 bg-[#FF0000]/5 p-3">
        <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#FF0000] inline-flex items-center gap-2">
          ▶ YouTube Music · Plays Inside the App
        </div>
        <p className="mt-1 text-[11px] text-white/55 leading-relaxed">
          Paste any YouTube video URL (https://www.youtube.com/watch?v=… or https://youtu.be/…). The song will autoplay inside the hologram modal. Legal because YouTube has the licensing.
        </p>
        <div className="mt-2 flex gap-2">
          <input
            value={youtubeUrlDraft}
            onChange={(e) => setYoutubeUrlDraft(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=…"
            data-testid="admin-youtube-url"
            className="flex-1 bg-[#050505] border border-white/15 text-white p-3 font-mono-ui text-xs focus:border-[#FF0000] focus:outline-none"
          />
          <button type="button" onClick={saveYoutubeUrl} disabled={savingYt} className="text-[10px] px-4 py-2 bg-[#FF0000] text-white hover:bg-white hover:text-[#FF0000] font-mono-ui tracking-[0.22em] uppercase font-bold disabled:opacity-60" data-testid="admin-youtube-url-save">
            {savingYt ? "..." : "Save"}
          </button>
        </div>
        {form.youtube_url && (
          <div className="mt-2 font-mono-ui text-[9px] tracking-[0.22em] uppercase text-white/40 truncate">
            Current: {form.youtube_url}
          </div>
        )}
      </div>

      <div className="mt-3 border border-[#FFD166]/40 bg-[#FFD166]/5 p-3">
        <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#FFD166] inline-flex items-center gap-2">
          ▶ Five-Song Setlist · Auto-Cycles On Stage
        </div>
        <p className="mt-1 text-[11px] text-white/55 leading-relaxed">
          Paste up to 5 YouTube URLs (one per line). The hologram performs to all 5 tracks back-to-back during the Investor Demo and inside the watch modal. Tracks that block embedding are auto-skipped.
        </p>
        <textarea
          value={setlistDraft}
          onChange={(e) => setSetlistDraft(e.target.value)}
          placeholder={"https://www.youtube.com/watch?v=…\nhttps://www.youtube.com/watch?v=…\nhttps://www.youtube.com/watch?v=…\nhttps://www.youtube.com/watch?v=…\nhttps://www.youtube.com/watch?v=…"}
          rows={6}
          data-testid="admin-setlist"
          className="mt-2 w-full bg-[#050505] border border-white/15 text-white p-3 font-mono-ui text-xs focus:border-[#FFD166] focus:outline-none"
        />
        <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
          <div className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-white/40">
            {setlistDraft.split(/\r?\n/).filter((s) => s.trim()).length} / 5 tracks queued
          </div>
          <button type="button" onClick={saveSetlist} disabled={savingSetlist} className="text-[10px] px-4 py-2 bg-[#FFD166] text-black hover:bg-white font-mono-ui tracking-[0.22em] uppercase font-bold disabled:opacity-60" data-testid="admin-setlist-save">
            {savingSetlist ? "..." : "Save Setlist"}
          </button>
        </div>
      </div>

      <TabAudioRecorder
        open={tabAudioOpen}
        onClose={() => setTabAudioOpen(false)}
        showId={showId}
        artist={form.artist}
        onJobStarted={(data) => {
          setVideoJob({ id: data.job_id, status: "pending", started_at: Date.now(), provider: "hedra" });
          setVideoElapsed(0);
        }}
      />
    </div>
  );
}
